import { formatAmount } from '../../utils/orderDisplay'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Field } from '@/components/ui/controls'
import { DataTable } from '@/components/ui/data-table'
import { toast as message } from 'sonner'
import { StatusBadge, CopyText } from '../products/PageHelpers'
import { Plus, LinkIcon, Copy } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getProducts, archiveProduct } from '../../services/product'
import { createCheckoutSession } from '../../services/checkout'
import { formatTimestamp } from '../../utils/format'
import type { Product, Pagination } from '../../types'

export default function ProductList() {
  const [products, setProducts] = useState<Product[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    size: 20,
    total: 0,
    totalPages: 0,
  })
  const [loading, setLoading] = useState(true)
  const [pageSize, setPageSize] = useState(20)
  const navigate = useNavigate()
  const [archiveTarget, setArchiveTarget] = useState<Product | null>(null)
  const [archiving, setArchiving] = useState(false)

  // Checkout 連結生成相關狀態
  const [linkModalVisible, setLinkModalVisible] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [generatingLink, setGeneratingLink] = useState(false)
  const [generatedLink, setGeneratedLink] = useState<string | null>(null)
  const [successUrl, setSuccessUrl] = useState('')
  const [cancelUrl, setCancelUrl] = useState('')

  const fetchProducts = (page: number = 1, size: number = pageSize) => {
    setLoading(true)
    getProducts({ page, size })
      .then((res) => {
        setProducts(res.data.items)
        setPagination(res.data.pagination)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchProducts()
  }, [])

  const handleArchive = async (id: string) => {
    try {
      await archiveProduct(id)
      message.success('產品已歸檔')
      fetchProducts(pagination.page)
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '操作失敗')
    }
  }

  const handleOpenLinkModal = (product: Product) => {
    setSelectedProduct(product)
    setGeneratedLink(null)
    setSuccessUrl('')
    setCancelUrl('')
    setLinkModalVisible(true)
  }

  const handleGenerateLink = async () => {
    try {
      if (
        !/^https?:\/\//.test(successUrl) ||
        (cancelUrl && !/^https?:\/\//.test(cancelUrl))
      ) {
        message.warning('請輸入有效的 URL')
        return
      }
      const values = { successUrl, cancelUrl }
      setGeneratingLink(true)
      const res = await createCheckoutSession({
        productId: selectedProduct!.id,
        successUrl: values.successUrl,
        cancelUrl: values.cancelUrl || undefined,
      })
      setGeneratedLink(res.data.url)
      message.success('Checkout 連結已生成')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '生成連結失敗')
    } finally {
      setGeneratingLink(false)
    }
  }

  const handleCopyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink)
      message.success('連結已複製到剪貼板')
    }
  }

  const handleCloseLinkModal = () => {
    setLinkModalVisible(false)
    setSelectedProduct(null)
    setGeneratedLink(null)
  }

  const columns = [
    {
      key: 'name',
      header: '產品名稱',
      className: 'min-w-64 max-w-md whitespace-normal',
      render: (row: Product) => (
        <div className="space-y-1">
          <div className="font-medium break-words">{row.name || '-'}</div>
          <div className="text-xs text-muted-foreground">
            <CopyText text={row.id} style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.id}</CopyText>
          </div>
        </div>
      ),
    },
    {
      key: 'price',
      header: '價格',
      className: 'text-right tabular-nums',
      render: (row: Product) => {
        const record = row
        return `${formatAmount(record.price)} ${record.currency}`
      },
    },
    {
      key: 'type',
      header: '計費類型',
      render: (row: Product) => {
        const record = row
        return (
          <StatusBadge
            color={record.productType === 'SUBSCRIPTION' ? 'blue' : 'default'}
          >
            {record.productType === 'SUBSCRIPTION' ? '訂閱' : '一次性付款'}
          </StatusBadge>
        )
      },
    },
    {
      key: 'status',
      header: '狀態',
      render: (row: Product) => {
        const status = row['status']
        return (
          <StatusBadge color={status === 'ACTIVE' ? 'green' : 'default'}>
            {status === 'ACTIVE' ? '在售' : '已歸檔'}
          </StatusBadge>
        )
      },
    },
    {
      key: 'createdAt',
      header: '創建時間',
      className: 'text-xs text-muted-foreground',
      render: (row: Product) => {
        const ts = row['createdAt']
        return formatTimestamp(ts)
      },
    },
    {
      key: 'actions',
      header: '操作',
      className: 'whitespace-nowrap',
      render: (row: Product) => {
        const record = row
        return (
          <div className="flex items-center gap-1 whitespace-nowrap">
            {record.status === 'ACTIVE' &&
              record.productType === 'ONE_TIME' && (
                <Button
                  onClick={() => handleOpenLinkModal(record)}
                  data-testid={'generate-link-btn'}
                  variant={'default'}
                  type="button"
                  size={'sm'}
                >
                  {<LinkIcon />}生成連結
                </Button>
              )}
            <Button
              onClick={() => navigate(`/dashboard/products/${record.id}`)}
              variant={'ghost'}
              className="text-muted-foreground"
              type="button"
              size={'sm'}
            >
              編輯
            </Button>
            {record.status === 'ACTIVE' && (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setArchiveTarget(record)}
              >
                歸檔
              </Button>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <h2
          style={{ margin: 0 }}
          className="mb-4 text-xl font-semibold tracking-tight"
        >
          產品管理
        </h2>
        <Button
          onClick={() => navigate('/dashboard/products/new')}
          data-testid={'create-product-btn'}
          variant={'default'}
          type="button"
        >
          {<Plus />}創建產品
        </Button>
      </div>
      <Card>
        <div className="space-y-4 px-5">
          <DataTable
            rowKey={'id'}
            loading={loading}
            columns={columns}
            data={products}
            pagination={{
              page: pagination.page,
              size: pagination.size,
              total: pagination.total,
              onChange: (page, size) => {
                if (size !== pageSize) {
                  setPageSize(size)
                  fetchProducts(1, size)
                } else {
                  fetchProducts(page, size)
                }
              },
            }}
          />
        </div>
      </Card>

      <Modal
        open={!!archiveTarget}
        onOpenChange={(open) => { if (!open && !archiving) setArchiveTarget(null) }}
        title="確定要歸檔此產品嗎？"
        children={archiveTarget?.name}
        footer={<>
          <Button variant="outline" disabled={archiving} onClick={() => setArchiveTarget(null)}>取消</Button>
          <Button disabled={archiving} onClick={async () => {
            if (!archiveTarget) return
            setArchiving(true)
            try { await handleArchive(archiveTarget.id); setArchiveTarget(null) }
            finally { setArchiving(false) }
          }}>確認</Button>
        </>}
      />

      {/* Checkout 連結生成 Modal */}
      <Modal
        title={generatedLink ? 'Checkout 連結已生成' : '生成 Checkout 連結'}
        open={linkModalVisible}
        footer={
          generatedLink
            ? [
                <Button
                  key="close"
                  onClick={handleCloseLinkModal}
                  variant={'outline'}
                  type="button"
                >
                  完成
                </Button>,
              ]
            : [
                <Button
                  key="close"
                  onClick={handleCloseLinkModal}
                  variant={'outline'}
                  type="button"
                >
                  取消
                </Button>,
                <Button
                  key="generate"
                  onClick={handleGenerateLink}
                  variant={'default'}
                  disabled={Boolean(generatingLink)}
                  type="button"
                >
                  生成連結
                </Button>,
              ]
        }
        onOpenChange={(open) => {
          if (!open) handleCloseLinkModal()
        }}
      >
        {generatedLink ? (
          <div className="space-y-4">
            <h3 className="font-semibold">連結已生成</h3>
            <p>連結有效期：24 小時</p>
            <div className="flex gap-2">
              <Input value={generatedLink} readOnly />
              <Button variant="outline" onClick={handleCopyLink}>
                <Copy />
                複製
              </Button>
            </div>
          </div>
        ) : (
          <>
            {selectedProduct && (
              <div
                style={{
                  marginBottom: 16,
                  padding: '12px 16px',
                  background: 'var(--landing-bg-primary)',
                  border: '1px solid var(--landing-border)',
                  borderRadius: 8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span
                  style={{ color: 'var(--landing-text-primary)' }}
                  className="font-semibold"
                >
                  {selectedProduct.name}
                </span>
                <span
                  style={{
                    color: 'var(--landing-accent-cyan)',
                    fontWeight: 600,
                    fontSize: 16,
                  }}
                >
                  {formatAmount(selectedProduct.price)}{' '}
                  {selectedProduct.currency}
                </span>
              </div>
            )}
            <div className="space-y-4">
              <Field label="成功跳轉 URL" htmlFor="success-url" required>
                <Input
                  id="success-url"
                  value={successUrl}
                  onChange={(event) => setSuccessUrl(event.target.value)}
                  placeholder="https://example.com/success"
                />
              </Field>
              <Field label="取消跳轉 URL（可選）" htmlFor="cancel-url">
                <Input
                  id="cancel-url"
                  value={cancelUrl}
                  onChange={(event) => setCancelUrl(event.target.value)}
                  placeholder="https://example.com/cancel"
                />
              </Field>
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}
